import React from 'react';

import delv from 'dlv';
import styled from 'styled-components';
import { formatDataQaIds } from 'utils';

import { IPickerAspectUiPattern, ISanityImage } from '@rbi-ctg/menu';
import IconCheck from 'components/icons/check/customizable-check';
import Picture from 'components/picture';
import PriceAndCalories from 'components/price-and-calories';
import { HiddenInput } from 'components/selection-indicator/indicator';
import { useMenuContext } from 'state/menu';
import { PickerAspectUiPattern } from 'state/menu/types';
import { nullNoop } from 'utils/noop';

import PickerAspectOptionContainer from './picker-aspect-option-container';
import { IPickerAspectOptionProps } from './types';

interface IAspectOptionContent extends IPickerAspectUiPattern {
  name: string;
  description?: string;
  calories?: string;
  icon?: React.FC<{ [k: string]: any }>;
  image?: ISanityImage;
  selected?: boolean;
  price: string;
}

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const TextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Name = styled.h3`
  font-size: 1.25rem;
  font-family: ${Styles.fontFamily.base};
  color: ${Styles.color.black};
  text-transform: ${Styles.textTransform.headlines};
  margin: 0;
  font-weight: ${Styles.fontWeight.normal};
  letter-spacing: 'normal';
`;

const Description = styled.span`
  margin: 0;
  font-size: 0.8125rem;
  color: ${Styles.color.grey.one};
`;

const PictureContainer = styled.div`
  height: 70px;
  width: 70px;
  margin-right: 13px;

  ${Styles.mobileTiny`
    height: 55px;
    width: 55px;
  `}
`;

const CheckContainer = styled.div`
  display: flex;
  width: 13px;
  height: 12px;
  margin-left: 16px;
`;

const AspectOptionContent: React.FC<IAspectOptionContent> = props => {
  const { showStaticMenu } = useMenuContext();
  return (
    <Container>
      <PictureContainer>
        {props.image && <Picture image={props.image} objectFitContain alt="" aria-hidden="true" />}
      </PictureContainer>
      <TextContainer>
        <Name>{props.name}</Name>
        <Description>{props.description}</Description>
        <PriceAndCalories
          isOffer={false}
          currentPrice={props.price}
          shouldHideCalories={!props.calories}
          formattedCalories={props.calories}
          shouldHidePrice={showStaticMenu}
        />
      </TextContainer>
    </Container>
  );
};

const PickerAspectOption = (props: IPickerAspectOptionProps) => {
  const handleSelection = (event: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
    event.preventDefault();

    if (props.disabled) {
      return;
    }
    props.handleSelection(props.option.identifier);
  };

  const name = props.option?.name?.locale;
  const uiPattern = delv(props, 'uiPattern', PickerAspectUiPattern.SELECTOR);

  return (
    <PickerAspectOptionContainer
      onClick={handleSelection}
      disabled={props.disabled}
      selected={props.selected}
      data-testid={`picker-aspect-option-${name}`}
      uiPattern={uiPattern}
    >
      <AspectOptionContent
        name={name}
        uiPattern={uiPattern}
        image={delv(props.option, 'image')}
        selected={props.selected}
        description={props.description}
        price={props.price}
        calories={props.calories}
      />
      <CheckContainer data-testid={`picker-option-${formatDataQaIds(name)}`}>
        <HiddenInput
          type="radio"
          name={props.inputName}
          checked={props.selected}
          disabled={props.disabled}
          onChange={nullNoop}
        />
        {props.selected && <IconCheck width="100%" height="100%" stroke={Styles.color.primary} />}
      </CheckContainer>
    </PickerAspectOptionContainer>
  );
};

export default PickerAspectOption;
